import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { APP_NAME, APP_TAGLINE, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";

// ─────────────────────────────────────────────────────────────────────────────
// Single star — used beside AI labels
// ─────────────────────────────────────────────────────────────────────────────
const ThreeStars = ({
  className = "text-[#2563EB]",
}: {
  className?: string;
}) => (
  <span
    className={`inline-flex items-center leading-none ${className}`}
    aria-hidden="true"
  >
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
    </svg>
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
// Mini icons (inline SVGs so no extra import dependency)
// ─────────────────────────────────────────────────────────────────────────────
const ShieldCheckIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const BellIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

const UploadIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);

const ChartBarIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const UserGroupIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const DocumentIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 13l4 4L19 7"
    />
  </svg>
);

const XMarkIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 8l4 4m0 0l-4 4m4-4H3"
    />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Mock Dashboard Preview (hero screenshot)
// ─────────────────────────────────────────────────────────────────────────────
const MockDashboard = () => (
  <div className="bg-white rounded-2xl shadow-2xl border border-[#E5E7EB] overflow-hidden text-xs">
    {/* Top bar */}
    <div className="bg-white border-b border-[#E5E7EB] px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-[#2563EB] rounded flex items-center justify-center">
          <SentrilogIcon className="w-3 h-3 text-white" />
        </div>
        <span className="font-semibold text-[#111827] text-[11px]">
          {APP_NAME}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button className="px-2 py-1 bg-[#2563EB] text-white rounded text-[10px] font-medium">
          + Add
        </button>
        <div className="w-6 h-6 bg-[#DBEAFE] rounded-full flex items-center justify-center text-[#2563EB] text-[9px] font-bold">
          JD
        </div>
      </div>
    </div>
    {/* Summary cards */}
    <div className="px-4 py-3 grid grid-cols-3 gap-2">
      <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">
        <p className="text-[#DC2626] font-bold text-base">4</p>
        <p className="text-[#EF4444] text-[10px] font-medium">High Risk</p>
        <p className="text-[#9CA3AF] text-[9px]">Expired or &lt;7 days</p>
      </div>
      <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg px-3 py-2">
        <p className="text-[#D97706] font-bold text-base">7</p>
        <p className="text-[#F59E0B] text-[10px] font-medium">Upcoming</p>
        <p className="text-[#9CA3AF] text-[9px]">Expiring in 30 days</p>
      </div>
      <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg px-3 py-2">
        <p className="text-[#16A34A] font-bold text-base">23</p>
        <p className="text-[#22C55E] text-[10px] font-medium">Active</p>
        <p className="text-[#9CA3AF] text-[9px]">All good</p>
      </div>
    </div>
    {/* Table header */}
    <div className="px-4 pb-1">
      <div className="grid grid-cols-5 gap-1 text-[9px] font-semibold text-[#6B7280] uppercase tracking-wide px-2 py-1 border-b border-[#F3F4F6]">
        <span className="col-span-2">Document</span>
        <span>Expiry</span>
        <span>Status</span>
        <span>Action</span>
      </div>
    </div>
    {/* Table rows */}
    {[
      {
        name: "ISO Certification",
        expiry: "Jan 15, 2025",
        status: "Expired",
        statusColor: "bg-[#FEF2F2] text-[#DC2626]",
        action: "Renew",
        actionColor: "text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2]",
      },
      {
        name: "Vendor Contract",
        expiry: "Mar 19, 2026",
        status: "High Risk",
        statusColor: "bg-[#FEF2F2] text-[#DC2626]",
        action: "Renew",
        actionColor: "text-[#DC2626] border-[#FECACA] hover:bg-[#FEF2F2]",
      },
      {
        name: "Office Insurance",
        expiry: "Apr 10, 2026",
        status: "Upcoming",
        statusColor: "bg-[#FFFBEB] text-[#D97706]",
        action: "Send Mail",
        actionColor: "text-[#D97706] border-[#FDE68A] hover:bg-[#FFFBEB]",
      },
      {
        name: "Business License",
        expiry: "Jun 30, 2026",
        status: "Active",
        statusColor: "bg-[#F0FDF4] text-[#16A34A]",
        action: "Send Mail",
        actionColor: "text-[#16A34A] border-[#BBF7D0] hover:bg-[#F0FDF4]",
      },
    ].map((row, i) => (
      <div key={i} className="px-4">
        <div className="grid grid-cols-5 gap-1 items-center px-2 py-2 border-b border-[#F9FAFB] hover:bg-[#F9FAFB] transition-colors">
          <span className="col-span-2 text-[#111827] font-medium text-[10px] truncate">
            {row.name}
          </span>
          <span className="text-[#6B7280] text-[10px]">{row.expiry}</span>
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium w-fit ${row.statusColor}`}
          >
            {row.status}
          </span>
          <button
            className={`px-1.5 py-0.5 rounded border text-[9px] font-medium bg-white ${row.actionColor} transition-colors`}
          >
            {row.action}
          </button>
        </div>
      </div>
    ))}
    {/* Bulk action hint */}
    <div className="px-4 py-2 bg-[#EFF6FF] border-t border-[#BFDBFE]">
      <p className="text-[#2563EB] text-[9px] font-medium">
        ✓ 2 selected · Bulk Renew · Bulk Remind · Export CSV
      </p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// FAQ Accordion Item
// ─────────────────────────────────────────────────────────────────────────────
const FaqItem: React.FC<{ question: string; answer: string }> = ({
  question,
  answer,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-[#F9FAFB] transition-colors"
      >
        <span className="font-semibold text-[#111827] text-sm">{question}</span>
        <span
          className={`text-[#6B7280] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        >
          <ChevronDownIcon />
        </span>
      </button>
      {open && (
        <div className="px-6 pb-4 bg-white">
          <p className="text-[#6B7280] text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Landing Page
// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// Book Demo Modal
// ─────────────────────────────────────────────────────────────────────────────
interface BookDemoModalProps {
  open: boolean;
  onClose: () => void;
}

const BookDemoModal: React.FC<BookDemoModalProps> = ({ open, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const overlayRef = useRef<HTMLDivElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError("Name, work email, and company are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post("/api/demo", form);
      setSuccess(true);
    } catch (err) {
      const msg =
        axios.isAxiosError(err) && err.response?.data?.error
          ? (err.response.data.error as string)
          : "Something went wrong. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({ name: "", email: "", company: "", phone: "", message: "" });
    setSuccess(false);
    setError("");
    onClose();
  };

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === overlayRef.current) handleClose();
      }}
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1D4ED8] to-[#2563EB] px-8 py-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-1">
                {APP_NAME}
              </p>
              <h2 className="text-white text-xl font-bold">Book a Demo</h2>
              <p className="text-blue-100 text-sm mt-1">
                We'll show you how {APP_NAME} fits your team — 20 min, no
                pressure.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-blue-200 hover:text-white transition-colors ml-4 mt-1"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {success ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#111827] mb-2">
                Request received!
              </h3>
              <p className="text-sm text-[#6B7280] mb-6">
                Thanks for reaching out. We'll be in touch within 1 business
                day. Check your inbox for a confirmation.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Alex Johnson"
                    className="w-full px-3 py-2.5 text-sm border border-[#D1D5DB] rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]
                               placeholder:text-[#9CA3AF] transition-colors"
                  />
                </div>
                {/* Company */}
                <div>
                  <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                    Company <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="company"
                    type="text"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Acme Corp"
                    className="w-full px-3 py-2.5 text-sm border border-[#D1D5DB] rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]
                               placeholder:text-[#9CA3AF] transition-colors"
                  />
                </div>
              </div>

              {/* Work Email */}
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                  Work Email <span className="text-red-500">*</span>
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="alex@company.com"
                  className="w-full px-3 py-2.5 text-sm border border-[#D1D5DB] rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]
                             placeholder:text-[#9CA3AF] transition-colors"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                  Phone{" "}
                  <span className="text-[#9CA3AF] font-normal">(optional)</span>
                </label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+1 555 000 0000"
                  className="w-full px-3 py-2.5 text-sm border border-[#D1D5DB] rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]
                             placeholder:text-[#9CA3AF] transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-semibold text-[#374151] mb-1.5">
                  What would you like to see?{" "}
                  <span className="text-[#9CA3AF] font-normal">(optional)</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="e.g. We manage 200+ contracts and need automated renewal alerts..."
                  className="w-full px-3 py-2.5 text-sm border border-[#D1D5DB] rounded-lg resize-none
                             focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB]
                             placeholder:text-[#9CA3AF] transition-colors"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold
                           rounded-xl transition-colors shadow-lg shadow-blue-500/20
                           disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8H4z"
                      />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    Book My Demo
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </>
                )}
              </button>

              <p className="text-center text-xs text-[#9CA3AF]">
                No commitment · We'll respond within 1 business day
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [showDemoModal, setShowDemoModal] = useState(false);

  return (
    <div className="min-h-screen bg-white font-sans">
      <BookDemoModal
        open={showDemoModal}
        onClose={() => setShowDemoModal(false)}
      />

      {/* ══════════════════════════════════════════════════════════════════════
          NAVBAR
      ══════════════════════════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a
              href="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
                <SentrilogIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-[#111827] text-lg">
                {APP_NAME}
              </span>
            </a>

            {/* Nav links */}
            <nav className="hidden md:flex items-center gap-6">
              <a
                href="#features"
                className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                Features
              </a>
              <a
                href="#how-it-works"
                className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                How It Works
              </a>
              <a
                href="/pricing"
                className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                Pricing
              </a>
              <a
                href="#faq"
                className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
              >
                FAQ
              </a>
            </nav>

            {/* CTAs */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="text-sm font-medium text-[#374151] hover:text-[#111827] transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => setShowDemoModal(true)}
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-[#2563EB]
                           border border-[#2563EB]/40 rounded-lg hover:bg-[#EFF6FF] transition-colors"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Book a Demo
              </button>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
              >
                Start Free
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO SECTION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#EFF6FF] to-white pt-16 pb-24">
        {/* Background blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#DBEAFE] rounded-full blur-3xl opacity-40 -translate-y-1/2" />
        <div className="absolute top-20 right-1/4 w-64 h-64 bg-[#EDE9FE] rounded-full blur-3xl opacity-30" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <div>
              {/* Domain badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#DBEAFE] border border-[#BFDBFE] rounded-full text-[#1D4ED8] text-xs font-semibold mb-5">
                <span className="w-2 h-2 bg-[#2563EB] rounded-full animate-pulse" />
                sentrilog.com
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#111827] leading-tight mb-6">
                One missed renewal.
                <br />
                One massive fine —{" "}
                <span className="text-[#2563EB]">never again.</span>
              </h1>

              <p className="text-lg text-[#6B7280] leading-relaxed mb-4 max-w-lg">
                Track licenses, contracts, insurance policies, and compliance
                documents in one place. Our AI scans your documents to extract
                expiry dates automatically — and an intelligent agent sends
                reminders before anything lapses.
              </p>

              {/* AI highlight pill */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#EFF6FF] border border-[#BFDBFE] rounded-full text-[#1D4ED8] text-xs font-semibold mb-6">
                <ThreeStars />
                AI-powered document scanning &amp; agent reminders
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 mb-10">
                <button
                  onClick={() => navigate("/login")}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold rounded-xl transition-colors shadow-lg shadow-blue-500/25 text-base"
                >
                  Start Free
                  <ArrowRightIcon />
                </button>
                <button
                  onClick={() => setShowDemoModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-[#F9FAFB] text-[#374151] font-semibold rounded-xl border border-[#E5E7EB] transition-colors text-base shadow-sm"
                >
                  <svg
                    className="w-4 h-4 text-[#2563EB]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  Book a Demo
                </button>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {[
                  "No credit card required",
                  "Free forever plan",
                  "Setup in 1 minute",
                ].map((t) => (
                  <div
                    key={t}
                    className="flex items-center gap-1.5 text-sm text-[#6B7280]"
                  >
                    <span className="text-[#22C55E]">
                      <CheckIcon />
                    </span>
                    {t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right — mock screenshot */}
            <div className="relative">
              <div className="relative z-10">
                <MockDashboard />
              </div>
              {/* Decorative shadow/glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#DBEAFE] to-[#EDE9FE] rounded-2xl blur-2xl opacity-30 scale-105" />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PRODUCT SCREENSHOT
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 bg-gradient-to-b from-white to-[#F9FAFB] overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#BFDBFE] to-transparent" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading */}
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              The Dashboard
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827] mb-3">
              Everything in one clear view
            </h2>
            <p className="text-[#6B7280] text-lg max-w-xl mx-auto">
              Instantly see what's expiring, what's missing, and what needs
              attention — before it becomes a problem.
            </p>
          </div>

          {/* Browser frame + screenshot */}
          <div className="relative mx-auto max-w-5xl">
            {/* Outer glow */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#DBEAFE] via-[#EDE9FE] to-[#DBEAFE] rounded-3xl blur-2xl opacity-40" />

            {/* Browser chrome */}
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-2xl shadow-blue-900/10 bg-white">
              {/* Browser top bar */}
              <div className="flex items-center gap-2 px-4 py-3 bg-[#F3F4F6] border-b border-[#E5E7EB]">
                {/* Traffic lights */}
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#FC5C65]" />
                  <div className="w-3 h-3 rounded-full bg-[#FED330]" />
                  <div className="w-3 h-3 rounded-full bg-[#26DE81]" />
                </div>
                {/* Address bar */}
                <div className="flex-1 mx-3">
                  <div className="max-w-xs mx-auto flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-md px-3 py-1">
                    <svg
                      className="w-3 h-3 text-[#9CA3AF] flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    <span className="text-xs text-[#6B7280] font-mono truncate">
                      app.sentrilog.com/items
                    </span>
                  </div>
                </div>
              </div>

              {/* Screenshot */}
              <div className="relative">
                <img
                  src="/app-screenshot.gif"
                  alt="Sentrilog dashboard — document expiry tracker"
                  className="w-full h-auto block"
                  loading="lazy"
                />
                {/* Fade-out at the bottom to blend into the page */}
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#F9FAFB] to-transparent pointer-events-none" />
              </div>
            </div>

            {/* Floating feature callout badges */}
            <div className="absolute -left-4 top-24 hidden lg:flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 shadow-lg shadow-black/5">
              <div className="w-8 h-8 bg-[#FEF2F2] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-base">🚨</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#111827]">
                  High Risk Alert
                </p>
                <p className="text-xs text-[#9CA3AF]">Expires within 7 days</p>
              </div>
            </div>

            <div className="absolute -right-4 top-40 hidden lg:flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 shadow-lg shadow-black/5">
              <div className="w-8 h-8 bg-[#EFF6FF] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-base">🤖</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#111827] flex items-center gap-1">
                  AI Extracted <ThreeStars className="text-[#2563EB]" />
                </p>
                <p className="text-xs text-[#9CA3AF]">Auto-detected expiry</p>
              </div>
            </div>

            <div className="absolute -left-4 bottom-28 hidden lg:flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-xl px-3 py-2 shadow-lg shadow-black/5">
              <div className="w-8 h-8 bg-[#F0FDF4] rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-base">🔔</span>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#111827]">
                  Smart Reminders
                </p>
                <p className="text-xs text-[#9CA3AF]">30 / 7 / 1 day alerts</p>
              </div>
            </div>
          </div>

          {/* Stats bar below screenshot */}
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-2xl mx-auto text-center">
            {[
              { value: "< 1 min", label: "to set up your first document" },
              { value: "3×", label: "faster than spreadsheets" },
              { value: "Zero", label: "missed renewals when configured" },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="text-2xl font-extrabold text-[#2563EB]">
                  {value}
                </p>
                <p className="text-xs text-[#9CA3AF] mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PROBLEM SECTION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827] mb-4">
              Managing expiry dates in spreadsheets is{" "}
              <span className="text-[#DC2626]">risky</span>
            </h2>
            <p className="text-[#6B7280] text-lg">
              Many teams track important documents using Excel sheets, calendar
              reminders, emails, or sticky notes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Old way */}
            <div className="bg-white rounded-2xl border border-[#FECACA] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#FEF2F2] rounded-xl flex items-center justify-center">
                  <span className="text-xl">📊</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#DC2626] uppercase tracking-wide">
                    The old way
                  </p>
                  <h3 className="font-bold text-[#111827]">
                    Spreadsheets & manual tracking
                  </h3>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  "Missed renewals go unnoticed",
                  "Compliance issues & costly fines",
                  "Operational delays from expired docs",
                  "Manually updating reminders in calendars",
                  "No visibility across team documents",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 text-sm text-[#374151]"
                  >
                    <span className="mt-0.5 text-[#EF4444] flex-shrink-0">
                      <XMarkIcon />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* New way */}
            <div className="bg-white rounded-2xl border border-[#BBF7D0] p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#F0FDF4] rounded-xl flex items-center justify-center">
                  <span className="text-xl">✅</span>
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#16A34A] uppercase tracking-wide">
                    With {APP_NAME}
                  </p>
                  <h3 className="font-bold text-[#111827]">
                    Automated & effortless
                  </h3>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  { text: "AI scans docs & auto-fills expiry dates", ai: true },
                  { text: "Agent sends reminders at 30, 7 & 1 day", ai: true },
                  { text: "Dashboard with high-risk alerts", ai: false },
                  { text: "Bulk manage hundreds of documents", ai: false },
                  { text: "Team-wide visibility & ownership", ai: false },
                ].map(({ text, ai }) => (
                  <li
                    key={text}
                    className="flex items-start gap-2 text-sm text-[#374151]"
                  >
                    <span className="mt-0.5 text-[#22C55E] flex-shrink-0">
                      <CheckIcon />
                    </span>
                    <span className="flex items-center gap-1.5">
                      {text}
                      {ai && <ThreeStars className="text-[#2563EB]" />}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Simple 3-step setup
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              How It Works
            </h2>
            <p className="mt-3 text-[#6B7280] text-lg max-w-xl mx-auto inline-flex items-center gap-2 flex-wrap justify-center">
              Upload a document, let{" "}
              <span className="inline-flex items-center gap-1 font-semibold text-[#2563EB]">
                <ThreeStars />
                AI
              </span>{" "}
              do the reading — your agent handles the rest automatically.
            </p>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Connector line (desktop) */}
            <div className="hidden md:block absolute top-10 left-[calc(16.7%+24px)] right-[calc(16.7%+24px)] h-0.5 bg-gradient-to-r from-[#DBEAFE] via-[#93C5FD] to-[#DBEAFE]" />

            {[
              {
                step: "01",
                icon: <UploadIcon />,
                color: "bg-[#EFF6FF] text-[#2563EB]",
                title: "Scan or Import Documents",
                desc: "Upload a PDF or photo — AI reads the document and automatically extracts the name, category, and expiry date. Or bulk-import via CSV.",
                aiTag: true,
              },
              {
                step: "02",
                icon: <ChartBarIcon />,
                color: "bg-[#FFFBEB] text-[#D97706]",
                title: "Track at a Glance",
                desc: "See upcoming, expired, and missing items in one clean dashboard. Filter by risk level to prioritise what matters most.",
                aiTag: false,
              },
              {
                step: "03",
                icon: <BellIcon />,
                color: "bg-[#F0FDF4] text-[#16A34A]",
                title: "Agent Sends Reminders",
                desc: "Your AI agent automatically emails owners 30 days, 7 days, and 1 day before expiry — even to people not registered in the app.",
                aiTag: true,
              },
            ].map(({ step, icon, color, title, desc, aiTag }) => (
              <div
                key={step}
                className="relative bg-[#F9FAFB] rounded-2xl p-8 text-center hover:shadow-md transition-shadow"
              >
                <div
                  className={`w-14 h-14 ${color} rounded-2xl flex items-center justify-center mx-auto mb-4`}
                >
                  {icon}
                </div>
                <div className="absolute top-4 right-4 text-xs font-bold text-[#E5E7EB]">
                  {step}
                </div>
                {aiTag && (
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-[#2563EB] text-[10px] font-bold">
                    <ThreeStars />
                    AI
                  </div>
                )}
                <h3 className="font-bold text-[#111827] text-lg mb-2">
                  {title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FEATURES
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="features" className="py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Everything you need
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              Built for Teams That Can't Afford Mistakes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: (
                  <svg
                    className="w-6 h-6"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
                  </svg>
                ),
                iconBg: "bg-[#EFF6FF] text-[#2563EB]",
                title: "AI Document Scanning",
                desc: "Upload any PDF, image, or scanned document — our AI reads it and automatically fills in the document name, category, expiry date, and notes. No typing required.",
                highlights: [
                  "PDF & image support",
                  "Auto-fills all fields",
                  "GPT-4o powered",
                  "Instant extraction",
                ],
                aiFeature: true,
              },
              {
                icon: <BellIcon />,
                iconBg: "bg-[#FFFBEB] text-[#D97706]",
                title: "AI Agent Reminders",
                desc: "An intelligent scheduling agent runs daily and automatically sends email reminders 30, 7, and 1 day before every expiry — to both registered users and external owners.",
                highlights: [
                  "30-day reminder",
                  "7-day reminder",
                  "1-day reminder",
                  "External owner emails",
                ],
                aiFeature: true,
              },
              {
                icon: <ChartBarIcon />,
                iconBg: "bg-[#F5F3FF] text-[#7C3AED]",
                title: "Expiry Dashboard & Analytics",
                desc: "See high-risk and upcoming expiries at a glance. Charts show category breakdowns, expiry timelines, and trends — so you always know the full picture.",
                highlights: [
                  "KPI summary cards",
                  "Trend charts",
                  "Category breakdown",
                  "Priority alerts",
                ],
                aiFeature: false,
              },
              {
                icon: <DocumentIcon />,
                iconBg: "bg-[#FFF7ED] text-[#EA580C]",
                title: "Bulk Document Management",
                desc: "Manage hundreds of items without clicking one by one. Select multiple documents and renew, remind, archive, snooze, or export them all at once.",
                highlights: [
                  "Bulk renew",
                  "Bulk snooze",
                  "Bulk archive",
                  "Export CSV",
                ],
                aiFeature: false,
              },
            ].map(({ icon, iconBg, title, desc, highlights, aiFeature }) => (
              <div
                key={title}
                className={`bg-white rounded-2xl border p-8 hover:shadow-lg transition-shadow ${aiFeature ? "border-[#BFDBFE]" : "border-[#E5E7EB]"}`}
              >
                <div className="flex items-start justify-between mb-5">
                  <div
                    className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center`}
                  >
                    {icon}
                  </div>
                  {aiFeature && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-[#2563EB] text-xs font-bold">
                      <ThreeStars />
                      AI Powered
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-[#111827] text-xl mb-3">
                  {title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-4">
                  {desc}
                </p>
                <div className="flex flex-wrap gap-2">
                  {highlights.map((h) => (
                    <span
                      key={h}
                      className="px-2.5 py-1 bg-[#F3F4F6] text-[#374151] text-xs font-medium rounded-full"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          USE CASES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Who uses it
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              Made for Every Team
            </h2>
            <p className="mt-3 text-[#6B7280] text-lg max-w-xl mx-auto">
              From HR to operations, any team that manages time-sensitive
              documents needs {APP_NAME}.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                emoji: "👩‍💼",
                title: "HR Teams",
                color: "border-[#BFDBFE] bg-[#EFF6FF]",
                accent: "text-[#1D4ED8]",
                items: [
                  "Employee visas & work permits",
                  "Training certifications",
                  "Employment contracts",
                  "Professional memberships",
                ],
              },
              {
                emoji: "⚙️",
                title: "Operations Teams",
                color: "border-[#FDE68A] bg-[#FFFBEB]",
                accent: "text-[#92400E]",
                items: [
                  "Vendor contracts",
                  "Permits & licenses",
                  "Compliance documents",
                  "Equipment maintenance logs",
                ],
              },
              {
                emoji: "🏢",
                title: "Small Businesses",
                color: "border-[#BBF7D0] bg-[#F0FDF4]",
                accent: "text-[#166534]",
                items: [
                  "Business licenses",
                  "Insurance policies",
                  "Service agreements",
                  "Product warranties",
                ],
              },
            ].map(({ emoji, title, color, accent, items }) => (
              <div key={title} className={`rounded-2xl border-2 ${color} p-8`}>
                <div className="text-4xl mb-4">{emoji}</div>
                <h3 className={`font-bold text-xl mb-4 ${accent}`}>{title}</h3>
                <ul className="space-y-2">
                  {items.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-2 text-sm text-[#374151]"
                    >
                      <span className="w-1.5 h-1.5 bg-[#2563EB] rounded-full flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SCREENSHOTS SECTION
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-gradient-to-b from-[#EFF6FF] to-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Product preview
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              See It In Action
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Screenshot 1 — Dashboard */}
            <div className="group">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm group-hover:shadow-xl transition-shadow">
                <div className="bg-[#F3F4F6] px-4 py-2 flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-[#EF4444] rounded-full" />
                  <div className="w-3 h-3 bg-[#F59E0B] rounded-full" />
                  <div className="w-3 h-3 bg-[#22C55E] rounded-full" />
                  <span className="text-[10px] text-[#9CA3AF] ml-2">
                    Dashboard
                  </span>
                </div>
                <div className="p-4 space-y-2">
                  <div className="grid grid-cols-3 gap-1.5">
                    <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-2 text-center">
                      <p className="text-[#DC2626] font-bold text-lg">4</p>
                      <p className="text-[#EF4444] text-[9px]">High Risk</p>
                    </div>
                    <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-2 text-center">
                      <p className="text-[#D97706] font-bold text-lg">7</p>
                      <p className="text-[#F59E0B] text-[9px]">Upcoming</p>
                    </div>
                    <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-2 text-center">
                      <p className="text-[#16A34A] font-bold text-lg">23</p>
                      <p className="text-[#22C55E] text-[9px]">Active</p>
                    </div>
                  </div>
                  <div className="h-24 bg-[#F9FAFB] rounded-xl flex items-center justify-center">
                    <div className="flex items-end gap-1 h-14 px-4">
                      {[40, 65, 55, 80, 45, 90, 70].map((h, i) => (
                        <div
                          key={i}
                          className="w-5 rounded-t"
                          style={{
                            height: `${h}%`,
                            backgroundColor: i === 5 ? "#2563EB" : "#BFDBFE",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-[#6B7280] font-medium mt-3">
                See expiring documents instantly
              </p>
            </div>

            {/* Screenshot 2 — Document table */}
            <div className="group">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm group-hover:shadow-xl transition-shadow">
                <div className="bg-[#F3F4F6] px-4 py-2 flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-[#EF4444] rounded-full" />
                  <div className="w-3 h-3 bg-[#F59E0B] rounded-full" />
                  <div className="w-3 h-3 bg-[#22C55E] rounded-full" />
                  <span className="text-[10px] text-[#9CA3AF] ml-2">
                    All Documents
                  </span>
                </div>
                <div className="p-4">
                  <div className="space-y-1.5">
                    {[
                      {
                        name: "ISO Cert",
                        badge: "Expired",
                        color: "bg-[#FEF2F2] text-[#DC2626]",
                      },
                      {
                        name: "Vendor Contract",
                        badge: "High Risk",
                        color: "bg-[#FEF2F2] text-[#DC2626]",
                      },
                      {
                        name: "Office Insurance",
                        badge: "Upcoming",
                        color: "bg-[#FFFBEB] text-[#D97706]",
                      },
                      {
                        name: "Business License",
                        badge: "Active",
                        color: "bg-[#F0FDF4] text-[#16A34A]",
                      },
                      {
                        name: "NDA Agreement",
                        badge: "Active",
                        color: "bg-[#F0FDF4] text-[#16A34A]",
                      },
                    ].map((r) => (
                      <div
                        key={r.name}
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-[#F9FAFB]"
                      >
                        <span className="text-[10px] text-[#374151] font-medium">
                          {r.name}
                        </span>
                        <span
                          className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${r.color}`}
                        >
                          {r.badge}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-[#6B7280] font-medium mt-3">
                Manage hundreds of documents easily
              </p>
            </div>

            {/* Screenshot 3 — Import */}
            <div className="group">
              <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-hidden shadow-sm group-hover:shadow-xl transition-shadow">
                <div className="bg-[#F3F4F6] px-4 py-2 flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-[#EF4444] rounded-full" />
                  <div className="w-3 h-3 bg-[#F59E0B] rounded-full" />
                  <div className="w-3 h-3 bg-[#22C55E] rounded-full" />
                  <span className="text-[10px] text-[#9CA3AF] ml-2">
                    Import
                  </span>
                </div>
                <div className="p-4 flex flex-col items-center justify-center h-[150px]">
                  <div className="w-14 h-14 bg-[#EFF6FF] rounded-2xl flex items-center justify-center mb-3">
                    <svg
                      className="w-7 h-7 text-[#2563EB]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-[#374151] mb-1">
                    Drop your CSV here
                  </p>
                  <p className="text-[10px] text-[#9CA3AF]">
                    or click to upload · Excel, CSV
                  </p>
                  <div className="mt-3 w-full bg-[#DBEAFE] rounded-full h-1.5">
                    <div className="bg-[#2563EB] h-1.5 rounded-full w-2/3 animate-pulse" />
                  </div>
                </div>
              </div>
              <p className="text-center text-sm text-[#6B7280] font-medium mt-3">
                Import from your existing spreadsheet
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          PRICING
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Simple pricing
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              Start Free. Scale as You Grow.
            </h2>
            <p className="mt-3 text-[#6B7280] text-lg max-w-xl mx-auto">
              Start free — no credit card required. Upgrade to Pro or Business
              anytime; checkout is powered by Stripe.
            </p>
            <div className="inline-flex items-center gap-2 mt-5 px-4 py-2 bg-[#EFF6FF] border border-[#BFDBFE] rounded-full text-xs font-semibold text-[#1D4ED8]">
              🔒 Secure payments via Stripe
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
            {[
              {
                key: "free",
                name: "Free",
                price: "$0",
                period: "forever",
                desc: "Perfect for individuals tracking personal or business docs.",
                highlight: false,
                cta: "Get Started Free",
                ctaAction: () => navigate("/login"),
                ctaDisabled: false,
                features: [
                  "Up to 15 documents",
                  "Email reminders (30, 7, 1 day)",
                  "5 AI scans / month",
                  "10 AI chat messages / day",
                  "Dashboard & risk overview",
                ],
              },
              {
                key: "pro",
                name: "Pro",
                price: "$15",
                period: "/month",
                desc: "Unlimited documents and CSV import for solo operators.",
                highlight: false,
                badge: null,
                cta: "Subscribe",
                ctaAction: () => navigate("/login"),
                ctaDisabled: false,
                features: [
                  "Unlimited documents",
                  "Up to 5 team members",
                  "100 AI scans / month",
                  "Unlimited AI chat",
                  "CSV import & export",
                  "Custom reminder intervals",
                  "Audit trail",
                  "Snooze reminders",
                ],
              },
              {
                key: "business",
                name: "Business",
                price: "$35",
                period: "/month",
                desc: "Full compliance suite for growing teams.",
                highlight: true,
                badge: "Most Popular",
                cta: "Subscribe",
                ctaAction: () => navigate("/login"),
                ctaDisabled: false,
                features: [
                  "Everything in Pro",
                  "Up to 25 team members",
                  "Unlimited AI scans",
                  "Custom email templates",
                  "Slack / webhook alerts",
                  "Multi-workspace",
                  "Priority support",
                ],
              },
              {
                key: "enterprise",
                name: "Enterprise",
                price: "Custom",
                period: "",
                desc: "For large organisations with complex compliance needs.",
                highlight: false,
                badge: null,
                cta: "Contact Us",
                ctaAction: () => window.open("mailto:hello@sentrilog.com?subject=Enterprise Enquiry", "_blank"),
                ctaDisabled: false,
                features: [
                  "Everything in Business",
                  "Unlimited team members",
                  "API access",
                  "White-label option",
                  "SSO / SAML",
                  "Dedicated account manager",
                  "SLA & uptime guarantee",
                  "Custom integrations",
                  "On-premise available",
                ],
              },
            ].map(({ key, name, price, period, desc, highlight, badge, cta, ctaAction, ctaDisabled, features }) => (
              <div
                key={key}
                className={`rounded-2xl p-7 flex flex-col relative ${
                  highlight
                    ? "bg-[#2563EB] text-white shadow-2xl shadow-blue-500/30 border-2 border-[#1D4ED8] scale-[1.02]"
                    : "bg-white border border-[#E5E7EB] shadow-sm hover:shadow-md transition-shadow"
                }`}
              >
                {badge && (
                  <span className={`inline-block px-2.5 py-1 text-[10px] font-bold rounded-full mb-3 w-fit ${highlight ? "bg-white/20 text-white" : "bg-[#DBEAFE] text-[#1D4ED8]"}`}>
                    {badge}
                  </span>
                )}
                <h3 className={`font-bold text-lg mb-1 ${highlight ? "text-white" : "text-[#111827]"}`}>
                  {name}
                </h3>
                <div className="flex items-baseline gap-0.5 mb-2">
                  <span className={`text-3xl font-extrabold ${highlight ? "text-white" : "text-[#111827]"}`}>
                    {price}
                  </span>
                  {period && (
                    <span className={`text-sm ml-1 ${highlight ? "text-blue-200" : "text-[#9CA3AF]"}`}>
                      {period}
                    </span>
                  )}
                </div>
                <p className={`text-xs mb-5 ${highlight ? "text-blue-100" : "text-[#6B7280]"}`}>
                  {desc}
                </p>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 flex-shrink-0 ${highlight ? "text-blue-200" : "text-[#22C55E]"}`}>
                        <CheckIcon />
                      </span>
                      <span className={highlight ? "text-blue-50" : "text-[#374151]"}>
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={ctaAction}
                  disabled={ctaDisabled}
                  className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                    ctaDisabled
                      ? "bg-white/20 text-white/50 cursor-not-allowed border border-white/20"
                      : highlight
                        ? "bg-white text-[#2563EB] hover:bg-blue-50"
                        : key === "enterprise"
                          ? "bg-[#111827] text-white hover:bg-[#1F2937]"
                          : "bg-[#EFF6FF] text-[#2563EB] hover:bg-[#DBEAFE] border border-[#BFDBFE]"
                  }`}
                >
                  {cta}
                </button>
              </div>
            ))}
          </div>

          {/* Bottom note */}
          <p className="text-center text-xs text-[#9CA3AF] mt-10">
            All plans include automated expiry reminders, a risk dashboard, and
            document management. No credit card required to start.{" "}
            <a href="mailto:hello@sentrilog.com" className="text-[#2563EB] hover:underline">
              Questions? Drop us a line.
            </a>
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SOCIAL PROOF (placeholder)
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-8">
            What teams are saying
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                quote: `${APP_NAME} replaced our messy spreadsheets and keeps our compliance documents under control. The automated reminders alone save us hours each month.`,
                name: "Sarah M.",
                role: "HR Manager, TechCo",
                avatar: "SM",
              },
              {
                quote:
                  "We used to miss vendor contract renewals all the time. Now we get reminders weeks in advance and renew with one click. Brilliant tool.",
                name: "David K.",
                role: "Operations Lead, BuildFast",
                avatar: "DK",
              },
            ].map(({ quote, name, role, avatar }) => (
              <div
                key={name}
                className="bg-white rounded-2xl border border-[#E5E7EB] p-8 text-left shadow-sm"
              >
                <div className="flex mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <svg
                      key={i}
                      className="w-4 h-4 text-[#F59E0B]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#374151] text-sm leading-relaxed mb-5 italic">
                  "{quote}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#DBEAFE] rounded-full flex items-center justify-center text-[#2563EB] text-xs font-bold">
                    {avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#111827]">
                      {name}
                    </p>
                    <p className="text-xs text-[#9CA3AF]">{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FAQ
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold text-[#2563EB] uppercase tracking-widest mb-2">
              Got questions?
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#111827]">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-3">
            <FaqItem
              question="Do I need to manually add every document?"
              answer={`No. You can import all your items instantly using a CSV or Excel file. Just export your existing spreadsheet and upload it — ${APP_NAME} handles the rest. You can also add items one by one if you prefer.`}
            />
            <FaqItem
              question="Will I receive reminders before expiry?"
              answer={`Yes. ${APP_NAME} automatically sends email alerts at 30 days, 7 days, and 1 day before each item's expiry date. You can also configure custom reminder schedules on the Team and Business plans.`}
            />
            <FaqItem
              question="Is my data secure?"
              answer="Yes. Your data is stored securely with industry-standard encryption, backed up regularly, and never shared with third parties. We take data privacy seriously."
            />
            <FaqItem
              question="Can I use it for multiple teams or departments?"
              answer="Absolutely. You can assign documents to specific owners or teams, and each team member can see their own items in the dashboard. The Team and Business plans are designed for multi-user workflows."
            />
            <FaqItem
              question="What happens when a document expires?"
              answer="When a document expires, it's flagged as 'High Risk' on your dashboard. You'll see a 'Renew' action button that lets you update the expiry date and log the renewal in the document history. You can also archive expired documents you no longer need."
            />
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-gradient-to-br from-[#1D4ED8] to-[#2563EB] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-blue-200 text-base font-semibold uppercase tracking-widest mb-3">
            {APP_NAME}
          </p>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-4">
            {APP_TAGLINE}
          </h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto">
            Join compliance teams that never miss a renewal. AI scans your
            documents, an agent handles the reminders. Free to start.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white hover:bg-blue-50 text-[#2563EB] font-bold rounded-xl transition-colors shadow-xl text-base"
            >
              Start Free
              <ArrowRightIcon />
            </button>
            <button
              onClick={() => setShowDemoModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl border border-white/30 transition-colors text-base"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Book a Demo
            </button>
          </div>
          <p className="mt-6 text-blue-200 text-sm">
            No credit card · Free forever plan · Setup in 1 minute
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FOOTER
      ══════════════════════════════════════════════════════════════════════ */}
      <footer className="bg-[#111827] text-[#9CA3AF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <a href="/" className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity w-fit">
                <div className="w-7 h-7 bg-[#2563EB] rounded-lg flex items-center justify-center">
                  <SentrilogIcon className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-white text-base">
                  {APP_NAME}
                </span>
              </a>
              <p className="text-sm font-semibold text-[#6B7280] italic mb-1">
                {APP_TAGLINE}
              </p>
              <p className="text-sm leading-relaxed">
                AI-powered expiry tracking with automated agent reminders.
                Trusted by compliance teams worldwide.
              </p>
              <p className="text-xs text-[#4B5563] mt-2">🌐 sentrilog.com</p>
              <p className="text-xs text-[#4B5563] mt-1">
                ✉️{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="hover:text-white transition-colors underline underline-offset-2"
                >
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Product</h4>
              <ul className="space-y-2">
                {[
                  { label: "Features", href: "/#features" },
                  { label: "How It Works", href: "/#how-it-works" },
                  { label: "FAQ", href: "/#faq" },
                  { label: "Pricing", href: "/pricing" },
                ].map(({ label, href }) => (
                  <li key={label}>
                    <a
                      href={href}
                      className="text-sm hover:text-white transition-colors"
                    >
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">
                Resources
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="text-sm hover:text-white transition-colors"
                  >
                    Contact Us
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${SUPPORT_EMAIL}?subject=Support%20Request`}
                    className="text-sm hover:text-white transition-colors"
                  >
                    Support
                  </a>
                </li>
                <li>
                  <a
                    href="/login"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Sign In
                  </a>
                </li>
                <li>
                  <a
                    href="/register"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Get Started Free
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-3">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/privacy"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/cookie-policy"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/refund"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Refund Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/pricing"
                    className="text-sm hover:text-white transition-colors"
                  >
                    Pricing
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-[#1F2937] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm">
              © {new Date().getFullYear()} {APP_NAME}. All rights reserved.
            </p>
            <p className="text-sm">
              Made with ❤️ for compliance teams everywhere
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
